import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authAPI, contactAPI } from '../utils/api';
import { useAuthStore } from '../store/authStore';
import Logo from './Logo';
import '../styles/Footer.css';

const emptyForm = { name: '', email: '', phone: '', address: '', message: '' };

const formatAddress = (address) => address
  ? [address.street, address.unit, address.city, address.province, address.postalCode]
      .filter(Boolean)
      .join(', ')
  : '';

const Footer = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (!isContactOpen) return undefined;
    nameInputRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsContactOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    document.body.classList.add('contact-modal-open');
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.classList.remove('contact-modal-open');
    };
  }, [isContactOpen]);

  const openContact = async () => {
    setContactForm({ ...emptyForm, name: user?.name || '', email: user?.email || '' });
    setIsContactOpen(true);
    if (isAuthenticated) {
      try {
        const { data } = await authAPI.getProfile();
        setContactForm((current) => ({
          ...current,
          name: data.name || current.name,
          email: data.email || current.email,
          phone: data.phone || '',
          address: formatAddress(data.address)
        }));
      } catch (error) {
        // The form remains usable with locally stored account information.
      }
    }
  };

  const updateContactField = (event) => {
    const { name, value } = event.target;
    setContactForm((current) => ({ ...current, [name]: value }));
  };

  const submitContact = async (event) => {
    event.preventDefault();
    try {
      setSending(true);
      await contactAPI.send(contactForm);
      toast.success(t('contact.sent'));
      setIsContactOpen(false);
      setContactForm(emptyForm);
    } catch (error) {
      toast.error(error.response?.status === 429 ? t('contact.tooMany') : t('contact.sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <div className="footer-logo"><Logo light /></div>
            <p>{t('footer.description')}</p>
          </div>

          <div className="footer-section">
            <button type="button" className="footer-contact-link" onClick={openContact}>
              {t('footer.contact')}
            </button>
          </div>

          <div className="footer-section">
            <h4>{t('nav.services')}</h4>
            <ul>
              <li><Link to="/services">{t('services.regular')}</Link></li>
              <li><Link to="/services">{t('services.deepCleaning')}</Link></li>
              <li><Link to="/services">{t('services.moveOut')}</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2026 Tout Propre. {t('footer.rights')}</p>
        </div>
      </footer>

      {isContactOpen && (
        <div
          className="contact-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsContactOpen(false);
          }}
        >
          <section className="contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-title">
            <div className="contact-modal-header">
              <h2 id="contact-title">{t('contact.title')}</h2>
              <button type="button" className="contact-close" onClick={() => setIsContactOpen(false)} aria-label={t('common.close')}>
                ×
              </button>
            </div>

            <form onSubmit={submitContact} className="contact-form">
              <label>{t('auth.name')}<input ref={nameInputRef} name="name" value={contactForm.name} onChange={updateContactField} maxLength="120" required /></label>
              <label>{t('auth.email')}<input name="email" type="email" value={contactForm.email} onChange={updateContactField} maxLength="254" required /></label>
              <label>{t('profile.phone')}<input name="phone" type="tel" value={contactForm.phone} onChange={updateContactField} maxLength="40" required /></label>
              <label>{t('contact.address')}<input name="address" value={contactForm.address} onChange={updateContactField} maxLength="500" /></label>
              <label>{t('contact.message')}<textarea name="message" value={contactForm.message} onChange={updateContactField} rows="6" maxLength="5000" required /></label>
              <button type="submit" className="contact-submit" disabled={sending}>
                {sending ? t('common.loading') : t('contact.send')}
              </button>
            </form>
          </section>
        </div>
      )}
    </>
  );
};

export default Footer;
